import React, { useState, useEffect } from 'react';
import { RSInput, RSButton } from 'reactsymbols-kit';
import Styles from '../../styles/colors';
import Spinner from '../Spinner';
import AddStateController from '../../merode/controllers/AddStateController';
import { useController, useAsynchronousAction } from '../../hooks';
import type { IModelVersion as IControllerModelVersion } from '../../merode/controllers/Controller';
import type { IModelVersion } from '../../stores/projects/model_version';
import type { Position, StateType } from '../../merode/commands/types';
import type Report from '../../merode/checks/Report';
import type { IObjectType } from '../../stores/model/metamodel/object_type';
import type { IFSM } from '../../stores/model/metamodel/fsm';
import { asRSChange, rsValue, type RSInputChange } from '../Inspector/rsForm';

/**
 * A typed controller method, re-typed to the loose action shape
 * `useAsynchronousAction` declares (it forwards args/results untyped). The
 * controller methods carry concrete signatures; the hook accepts `(...args:
 * unknown[]) => Promise<unknown>`. This narrow cast bridges the two at the call
 * site without touching runtime — the same typed args flow through `execute`.
 */
type AsyncActionLike = (...args: unknown[]) => Promise<unknown>;

interface AddStateFormProps {
  /** The ModelVersion to add the state to. */
  modelVersion: IModelVersion;
  /** The ObjectType of the FSM to add the state to. */
  objectType: IObjectType;
  /** The FSM to add the state to. */
  fsm: IFSM;
  /** The type of the new state. */
  type: StateType | null;
  /** The position of the new state. */
  position?: Position | null;
  /** Callback to cancel adding a state. */
  onCancel?: () => void;
  /** Callback to send the report up. */
  onReport?: (report: Report) => void;
}

/**
 * Form to add an FSM state
 *
 * Props:
 * - modelVersion: the ModelVersion to add the state to.
 * - objectType: the ObjectType of the FSM to add the state to.
 * - fsm: the FSM to add the state to.
 * - type: the type of the new state.
 * - position: the position of the new state.
 * - onCancel: callback to cancel adding a state.
 * - onReport: callback to send the report up.
 */
const AddStateForm = ({ modelVersion, objectType, fsm, type, position, onCancel, onReport }: AddStateFormProps) => {
  // The controller stores `objectType`/`fsm` and forwards them to
  // `Combined_CreateState`, whose params are `OrId<IObjectType>` / `OrId<IFSM>`
  // (instance OR id). The controller constructor over-narrows them to `string`;
  // the instances are valid at runtime. The store `IModelVersion` is the same
  // loaded node the controller's narrower `IModelVersion` describes (the two
  // `IModel` surfaces coincide at runtime). Cast at this construction boundary
  // only — same pattern as the committed diagram controllers.
  const controller = useController(
    () =>
      new AddStateController(
        modelVersion as unknown as IControllerModelVersion,
        objectType as unknown as string,
        fsm as unknown as string
      )
  );
  // `useAsynchronousAction` types the success result as `unknown` (it forwards
  // whatever the action resolves to); `addState` resolves a `Report`. Cast at
  // this single boundary — runtime value unchanged.
  const addStateAction = useAsynchronousAction(controller.addState as AsyncActionLike, report => {
    if (onReport != null) {
      onReport(report as Report);
    }
  });
  const [name, setName] = useState('');
  const nameCheck = controller.checkNameInput(name);
  const valid = name != null && name.length > 0 && nameCheck.ok;

  const busy = addStateAction.busy;

  let typeString = '';
  if (type === 'INITIAL_STATE') {
    typeString = 'initial';
  } else if (type === 'FINAL_STATE') {
    typeString = 'final';
  }

  useEffect(() => {
    if (type === 'INITIAL_STATE') {
      // For an initial state, we don't need any input, so fire right away
      addStateAction.execute('initial', type, position);
    }
  }, [type]);

  return (
    <div className="merlin-modal add-state-form">
      <div className="title">Add {typeString} state</div>
      {type === 'INITIAL_STATE' ? (
        <div className="add-initial-state-spinner">
          <Spinner />
        </div>
      ) : (
        <React.Fragment>
          <form
            className="form"
            onSubmit={e => {
              e.preventDefault();
              if (valid && !busy) {
                addStateAction.execute(name, type, position);
              }
            }}
          >
            <div className="modal-body">
              <div className="container">
                <div className="row">
                  <div className="col">
                    <RSInput
                      autoFocus={true}
                      name="name"
                      label="Name"
                      value={name}
                      onChange={asRSChange<RSInputChange>((value: string) => setName(value))}
                      level={rsValue(nameCheck.ok ? null : 'danger')}
                      text={rsValue(nameCheck.ok ? null : nameCheck.message)}
                      disabled={busy}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="button-wrapper">
              <RSButton
                value="Cancel"
                type="button"
                background={Styles.defaultGradient}
                color={Styles.darkTextColor}
                onClick={onCancel}
              />
              <RSButton
                value={'Add'}
                type="submit"
                disabled={!valid || busy}
                iconName={rsValue(busy ? 'FaSpinner' : null)}
                iconSize={15}
                className={rsValue(busy ? 'spin' : null)}
              />
            </div>
          </form>
        </React.Fragment>
      )}
    </div>
  );
};

export default AddStateForm;
